import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ArticleDisposition,
  PackageSpeed,
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigType,
} from '../_models/shipping-config.interface';

/** Pro Operation berechnete Default-Versandkonfiguration vom Backend.
 * scope:
 *   'internal'           — interner Auftrag → Technikerversand
 *   'external_staging'   — externer Auftrag mit Staging-Artikel → Technikerversand
 *                          mit Auftragstechniker als Ziel
 *   'external_material'  — externer Auftrag mit priority.material_shipping →
 *                          Filialversand
 *   'external_other'     — sonstige externe Auftraege → Filialversand mit
 *                          Material-Status-Logik
 *   'cloned'             — Klone der zuletzt archivierten Config (Nachversand)
 * form_data ist direkt als ShippingConfigFormData verwendbar. */
export interface ShippingConfigDefaultPreview {
  operation_id: number;
  scope: 'internal' | 'external_staging' | 'external_material' | 'external_other' | 'cloned';
  form_data: {
    shipping_type: ShippingConfigType;
    shipment_method: ShipmentMethod;
    package_speed?: PackageSpeed | null;
    article_disposition?: ArticleDisposition;
    pickpoint_station_id?: number | null;
    target_user_id?: number | null;
    target_warehouse_id?: number | null;
    target_store_id?: number | null;
    shipping_date?: string | null;
    notes?: string | null;
    pm_code?: string | null;
    // Regel-/Offset-Felder, die preview_attrs liefert — muessen mit ins
    // ShippingConfigFormData, sonst geht "Vom Auftrag" / "X Werktage vor Auftrag"
    // beim Anlegen der Default-Configs verloren.
    target_user_from_operation?: boolean;
    shipping_date_offset_days?: number | null;
    staging_ship_offset_days?: number | null;
    staging_ship_ready_at?: string | null;
    technician_ship_target?: 'private' | 'warehouse' | null;
    target_customer_id?: number | null;
    pickup_date?: string | null;
  };
}

/** Request-Body fuer create/update einer Versandkonfiguration. */
export interface ShippingConfigPayload {
  shipping_type: ShippingConfigType;
  shipment_method: ShipmentMethod;
  package_speed?: PackageSpeed | null;
  pickpoint_station_id?: number | null;
  target_user_id?: number | null;
  target_warehouse_id?: number | null;
  /** Regel "Ziel-Techniker vom Auftrag" — kein fester target_user_id. */
  target_user_from_operation?: boolean;
  shipping_date?: string | null;
  /** Offset-Regel "X Werktage vor Auftragsdatum" (normaler Versand / Staging). */
  shipping_date_offset_days?: number | null;
  staging_ship_offset_days?: number | null;
  pm_code?: string | null;
  /**
   * Abholzeitpunkt am PickPoint (ISO-8601). Nur bei `shipping_type === 'pickpoint'`
   * relevant. Backend setzt Default = operation_date - 1h, wenn leer beim
   * Anlegen einer PickPoint-Config.
   */
  pickup_date?: string | null;
  /** Frueheste-Versand-Zeitpunkt fuer Staging-Pakete (ISO-8601) oder null. */
  staging_ship_ready_at?: string | null;
  notes?: string;
}

@Injectable({ providedIn: 'root' })
export class ShippingConfigService {
  private readonly basePath = environment.apiURL + 'shipping_configs';

  constructor(private http: HttpClient) {}

  async getShippingConfig(operationId: number): Promise<ShippingConfig | null> {
    try {
      return await firstValueFrom(
        this.http.get<ShippingConfig | null>(`${this.basePath}/${operationId}.json`, {
          withCredentials: true,
        }),
      );
    } catch {
      return null;
    }
  }

  /** Phase 3: alle AKTIVEN Configs einer Operation (mehrere moeglich) inkl.
   *  requirement_split. Basis fuer die Versand-Matrix auf der Ticketseite. */
  async getActiveShippingConfigs(operationId: number): Promise<ShippingConfig[]> {
    try {
      return await firstValueFrom(
        this.http.get<ShippingConfig[]>(`${this.basePath}/${operationId}/active.json`, {
          withCredentials: true,
        }),
      );
    } catch {
      return [];
    }
  }

  /** Phase 3 (Matrix): ALLE Configs einer Operation — archivierte (abgeschlossene)
   *  zuerst, dann aktive — inkl. `archived`-Flag und `requirement_committed`
   *  (versandte/vorbereitete Mengen je operation_requirement_id). Datenbasis fuer
   *  die Versand-Matrix auf der Ticketseite. */
  async getMatrixConfigs(operationId: number): Promise<ShippingConfig[]> {
    try {
      return await firstValueFrom(
        this.http.get<ShippingConfig[]>(`${this.basePath}/${operationId}/matrix.json`, {
          withCredentials: true,
        }),
      );
    } catch {
      return [];
    }
  }

  /** Phase 3: Mengen-Split einer konkreten Config setzen (Replace-Semantik).
   *  requirements = [{ operation_requirement_id, quantity }]. */
  async saveConfigSplit(
    operationId: number,
    configId: number,
    requirements: Array<{ operation_requirement_id: number; quantity: number }>,
  ): Promise<ShippingConfig> {
    return firstValueFrom(
      this.http.post<ShippingConfig>(`${this.basePath}.json`, {
        operation_id: operationId,
        config_id: configId,
        requirements,
      }, { withCredentials: true }),
    );
  }

  /** Phase 3: eine konkrete aktive Config (per config_id) mit den Form-Daten
   *  aktualisieren. Laesst den Mengen-Split unangetastet (kein requirements). */
  async updateConfigById(
    operationId: number,
    configId: number,
    data: ShippingConfigPayload,
  ): Promise<ShippingConfig> {
    return firstValueFrom(
      this.http.post<ShippingConfig>(`${this.basePath}.json`, {
        operation_id: operationId,
        config_id: configId,
        ...data,
      }, { withCredentials: true }),
    );
  }

  /** Phase 3: eine ZUSAETZLICHE aktive Config anlegen (additional=true). */
  async createAdditionalConfig(
    operationId: number,
    data: ShippingConfigPayload,
  ): Promise<ShippingConfig> {
    return firstValueFrom(
      this.http.post<ShippingConfig>(`${this.basePath}.json`, {
        operation_id: operationId,
        additional: true,
        ...data,
      }, { withCredentials: true }),
    );
  }

  /** Phase 3: eine konkrete aktive Config per ID loeschen. */
  async deleteConfigById(operationId: number, configId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.basePath}/${operationId}.json`, {
        params: { config_id: String(configId) },
        withCredentials: true,
      }),
    );
  }

  async getArchivedShippingConfigs(operationId: number): Promise<ShippingConfig[]> {
    try {
      return await firstValueFrom(
        this.http.get<ShippingConfig[]>(`${this.basePath}/${operationId}/archived.json`, {
          withCredentials: true,
        }),
      );
    } catch {
      return [];
    }
  }

  async getContext(operationId: number): Promise<ShippingConfigContext> {
    return firstValueFrom(
      this.http.get<ShippingConfigContext>(
        `${this.basePath}/${operationId}/context.json`, { withCredentials: true }
      )
    );
  }

  /** Default-Vorlaufzeiten (Werktage vor Auftragsdatum) je Auftragsgruppe —
   * Single Source of Truth fuers Bulk-Excel-Auto-Create-Modal. Liefert die
   * Backend-Konstante GROUP_SHIPPING_OFFSET_DAYS (z.B. { internal: 0,
   * external_material: 0, external_staging: 12, external_other: 12 }). */
  async getGroupOffsetDefaults(): Promise<Record<string, number>> {
    const result = await firstValueFrom(
      this.http.get<{ group_offset_defaults: Record<string, number> }>(
        `${this.basePath}/group_offset_defaults.json`,
        { withCredentials: true },
      ),
    );
    return result?.group_offset_defaults ?? {};
  }

  /** Holt die zentrale PickPoint-Master-PM-Nummer (User ID 1). Wird beim
   * Anlegen einer neuen PickPoint-Versandconfig als Default-PM-Code im
   * Formular eingetragen. Liefert null wenn nicht gesetzt. */
  async getMasterPmCode(): Promise<string | null> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ pm_code: string | null }>(
          `${this.basePath}/master_pm_code.json`,
          { withCredentials: true },
        ),
      );
      return result.pm_code;
    } catch {
      return null;
    }
  }

  async createOrUpdateShippingConfig(
    operationId: number,
    data: ShippingConfigPayload,
  ): Promise<ShippingConfig> {
    return firstValueFrom(
      this.http.post<ShippingConfig>(`${this.basePath}.json`, {
        operation_id: operationId,
        ...data,
      }, { withCredentials: true })
    );
  }

  async deleteShippingConfig(operationId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.basePath}/${operationId}.json`, {
        withCredentials: true,
      }),
    );
  }

  async bulkCreate(
    operationIds: number[],
    data: ShippingConfigPayload,
  ): Promise<{ success: boolean; created: number; updated: number }> {
    return firstValueFrom(
      this.http.post<{ success: boolean; created: number; updated: number }>(
        `${this.basePath}/bulk.json`,
        { operation_ids: operationIds, ...data },
        { withCredentials: true }
      )
    );
  }

  /** Berechnet — ohne zu persistieren — Default-Versandkonfigurationen fuer
   * eine Liste von Operations nach den Backend-Auto-Create-Regeln. Vorlauf-
   * zeit pro Auftragskategorie verschiebt das shipping_date um N Tage VOR
   * das operation_date.
   *
   * `pendingStagingOpIds` listet Operationen, die im Bulk-Excel-Diff neue
   * STAGING-Requirements bekommen, aber noch nicht persistiert sind. Ohne
   * diesen Hint klassifiziert das Backend solche Auftraege faelschlich als
   * external_other (Filialversand), da die DB noch keinen STAGING-Eintrag
   * sieht. */
  async previewDefaults(
    operationIds: number[],
    leadTimes: {
      internal: number;
      external_staging: number;
      external_material: number;
      external_other: number;
    },
    pendingStagingOpIds: number[] = [],
    options: {
      assignments?: {
        external_staging?: 'technician' | 'store';
        external_material?: 'technician' | 'store';
        external_other?: 'technician' | 'store';
      };
      cloneArchived?: boolean;
    } = {},
  ): Promise<ShippingConfigDefaultPreview[]> {
    const a = options.assignments ?? {};
    const response = await firstValueFrom(
      this.http.post<{ results: ShippingConfigDefaultPreview[] }>(
        `${this.basePath}/preview_defaults`,
        {
          operation_ids: operationIds,
          pending_staging_operation_ids: pendingStagingOpIds,
          lead_time_internal_days: leadTimes.internal,
          lead_time_external_staging_days: leadTimes.external_staging,
          lead_time_external_material_days: leadTimes.external_material,
          lead_time_external_other_days: leadTimes.external_other,
          assign_external_staging: a.external_staging ?? null,
          assign_external_material: a.external_material ?? null,
          assign_external_other: a.external_other ?? null,
          clone_archived: options.cloneArchived ?? true,
        },
        { withCredentials: true },
      ),
    );
    return response.results;
  }

  async bulkByTechnician(
    data: ShippingConfigPayload & { technician_id: number; date_from: string; date_to: string },
  ): Promise<{ success: boolean; created: number; updated: number; total_operations: number }> {
    return firstValueFrom(
      this.http.post<{ success: boolean; created: number; updated: number; total_operations: number }>(
        `${this.basePath}/bulk_by_technician.json`,
        data,
        { withCredentials: true }
      )
    );
  }
}
